import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Eye, EyeOff, X } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import CountrySelect, { COUNTRIES } from './CountrySelect';
import { requestOtp, verifyOtp } from '../../lib/api';
import TelegramOtpStep from './TelegramOtpStep';

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} className="fill-current">
      <path d="M19.11 17.205c-.372 0-.892 1.044-1.353.974-.428-.075-.901-.27-1.327-.555-1.13-.747-1.916-1.477-2.6-2.6-.285-.426-.48-.9-.554-1.327-.07-.46.974-.981.974-1.353 0-.371-.974-1.59-1.299-1.946-.279-.305-.587-.34-.722-.341-.186 0-.4-.046-.611-.046-.211 0-.55.082-.835.387-.286.305-1.092 1.066-1.092 2.604 0 1.537 1.118 3.022 1.273 3.23.155.21 2.198 3.357 5.327 4.706.745.32 1.327.512 1.781.656.748.237 1.43.204 1.969.124.6-.09 1.846-.755 2.107-1.486.26-.73.26-1.358.182-1.486-.077-.128-.286-.205-.6-.36-.313-.155-1.847-.91-2.133-1.014-.286-.103-.494-.155-.703.155-.21.31-.808 1.014-.99 1.224-.181.21-.363.236-.677.082zM16 4C9.373 4 4 9.373 4 16c0 2.244.611 4.345 1.676 6.149L4 28l5.998-1.654A11.94 11.94 0 0016 28c6.627 0 12-5.373 12-12S22.627 4 16 4z" />
    </svg>
  );
}

export default function SignupModal({ open, onOpenChange, onSwitchToLogin }) {
  const { data } = useSite();
  const { refresh } = useAuth();
  const m = data.signupModal || {};

  const [step, setStep] = useState(1); // 1 = form, 2 = otp
  const [name, setName] = useState('');
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [mobile, setMobile] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpInfo, setOtpInfo] = useState(null); // { channels, deep_link, bot_username, fullMobile }

  const reset = () => {
    setStep(1); setName(''); setMobile(''); setPwd(''); setOtpInfo(null);
  };

  const startOtp = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return toast.error('Enter your name');
    if (mobile.length < 6) return toast.error('Enter a valid mobile number');
    if (pwd.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    const fullMobile = country.code.replace('+', '') + mobile;
    try {
      const info = await requestOtp(fullMobile, 'signup');
      setOtpInfo({ ...info, fullMobile });
      setStep(2);
      const chs = (info?.channels || []).map((c) => c === 'whatsapp' ? 'WhatsApp' : c === 'sms' ? 'SMS' : 'Telegram').join(' + ');
      toast.success(chs ? `OTP sent via ${chs}` : 'OTP sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to request OTP');
    } finally { setLoading(false); }
  };

  const verify = async (code) => {
    if (!otpInfo) return;
    try {
      await verifyOtp({ mobile: otpInfo.fullMobile, code, purpose: 'signup', name: name.trim(), password: pwd });
      toast.success(`Welcome, ${name.split(' ')[0]}!`);
      await refresh();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired OTP');
      throw err;
    }
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[440px] bg-[#1a1a1a] border border-zinc-800 text-zinc-100 p-0 sm:rounded-xl [&>button]:hidden">
        <DialogTitle className="sr-only">{step === 1 ? 'Sign Up' : 'Verify OTP'}</DialogTitle>
        <DialogDescription className="sr-only">Create an account and verify via Telegram OTP</DialogDescription>
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.06]">
          <WhatsAppIcon size={260} />
        </div>

        <span className="absolute right-3 top-3 z-20">
          <button onClick={() => handleClose(false)} className="w-8 h-8 rounded-full text-zinc-300 hover:text-white bg-black/30 hover:bg-red-500/80 border border-white/10 flex items-center justify-center transition" data-testid="signup-close-btn" aria-label="Close sign up">
            <X size={18} />
          </button>
        </span>

        <div className="relative z-10 flex items-center justify-between px-5 pt-4 pb-1.5">
          <h2 className="text-2xl font-bold text-white" data-testid="signup-title">{step === 1 ? (m.title || 'Sign Up') : 'Verify OTP'}</h2>
          <div className="flex items-center gap-1.5 mr-6">
            {data.branding.logoImage && (
              <img src={data.branding.logoImage} alt="logo" className="h-7 w-auto object-contain" />
            )}
            <div className="flex items-baseline gap-0.5">
              <span className="font-display text-xl font-extrabold tracking-[0.12em] text-[color:var(--gold)]">{data.branding.logoText}</span>
              <span className="font-display text-[9px] font-bold text-[color:var(--gold)]/80">{data.branding.logoAccent}</span>
            </div>
          </div>
        </div>

        {step === 1 ? (
          <form onSubmit={startOtp} className="relative z-10 px-5 pb-5 space-y-3">
            <div>
              <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">{m.nameLabel || 'Full Name'}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={m.namePlaceholder || 'Your Name'} className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-500" data-testid="signup-name-input" />
            </div>

            <div>
              <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">{m.mobileLabel || 'Mobile Number'}</label>
              <div className="flex items-stretch bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden focus-within:border-[color:var(--gold)]/50 relative">
                <CountrySelect value={country} onChange={setCountry} />
                <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 15))} placeholder={m.placeholder || 'Phone Number'} className="flex-1 h-11 bg-transparent px-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none pr-10" data-testid="signup-mobile-input" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-zinc-500">{mobile.length}</span>
              </div>
            </div>

            <div>
              <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">{m.passwordLabel || 'Password'}</label>
              <div className="relative">
                <Input type={showPwd ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={m.passwordPlaceholder} className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 pr-10" data-testid="signup-password-input" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                  {showPwd ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full h-11 rounded-md bg-[color:var(--gold)] hover:brightness-110 disabled:opacity-60 text-black font-bold text-[15px] transition click-ripple" data-testid="signup-submit-btn">
              {loading ? 'Sending OTP…' : 'Send OTP'}
            </button>

            <div className="flex items-center gap-3 text-xs text-zinc-500 py-0.5">
              <div className="flex-1 h-px bg-zinc-800" />{m.orText || 'Or'}<div className="flex-1 h-px bg-zinc-800" />
            </div>

            <a href={data.whatsappLink} target="_blank" rel="noreferrer" className="w-full h-11 rounded-md bg-[#25d366] hover:bg-[#1ebd5d] text-white font-bold text-[15px] flex items-center justify-center gap-2 transition">
              <WhatsAppIcon size={18} /> {m.whatsappBtn}
            </a>

            <div className="text-center text-[13px] text-zinc-300 pt-1">
              {m.alreadyText || 'Already have an account?'}{' '}
              <button type="button" onClick={onSwitchToLogin} className="text-white font-bold underline" data-testid="signup-switch-login-btn">{m.loginLink || 'Login'}</button>
            </div>
          </form>
        ) : (
          <div className="relative z-10 px-5 pb-5">
            <TelegramOtpStep info={otpInfo} onVerify={verify} onBack={() => setStep(1)} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
